/**
 * SNS Post Listener
 *
 * Listener layer: registers event listeners only.
 */

import { Roles } from "../../constants/roles";
import { getEventScope } from "../../core/event-scope/event-scope";

type SnsPostListenerHandler = {
    handleSnsInput?: (ev: Event) => void;
    handleSnsPreviewActivate?: (ev: Event) => void;
};

export class SNSPostListener {
    private static findPanelInRoot(root: HTMLElement | Document, postKey: string): HTMLElement | null {
        const searchRoot = root instanceof Document ? root.documentElement : root;
        if (!searchRoot) {
            return null;
        }

        const elements = searchRoot.getElementsByTagName("*");
        for (const element of Array.from(elements)) {
            if (!(element instanceof HTMLElement)) {
                continue;
            }

            if (
                element.getAttribute("data-role") === Roles.SNS_LINKS_PANEL &&
                element.getAttribute("data-post-id") === postKey
            ) {
                return element;
            }
        }

        return null;
    }

    /**
     * Register listeners related to sns_links[] inputs.
     */
    public static registerSnsLinksListeners(params: {
        postKey: string;
        rootEl: Document | HTMLElement;
        handler: SnsPostListenerHandler;
    }): void {
        const { postKey, rootEl, handler } = params;
        const scope = getEventScope(rootEl, `post-sns-links:${String(postKey)}`);

        scope.once("sns-links", () => {
            // NOTE: Do NOT bind `[data-role="sns-open"]` here.
            // That role belongs to sns-links-panel (V2). Adding inputs is handled via the panel's external handler hook.

            if (handler.handleSnsInput) {
                scope.delegate("input", `[data-role="${Roles.SNS_LINK_INPUT}"]`, (ev) => {
                    handler.handleSnsInput?.call(handler, ev);
                });
            }

            if (handler.handleSnsPreviewActivate) {
                scope.delegate("click", `[data-role="${Roles.SNS_PREVIEW_TILE}"]`, (ev) => {
                    handler.handleSnsPreviewActivate?.call(handler, ev);
                });
            }
        });
    }

    /**
     * Register SNS auto-detect from textarea (debounced).
     *
     * Listener responsibility:
     * - bind the input listener once
     * - debounce execution
     * - skip while sns-links-panel is open (is-editing)
     * The actual detection/merge logic is provided by callback.
     */
    public static registerContentTextareaAutoDetect(params: {
        postKey: string;
        formRoot: HTMLElement | Document;
        contentTextarea: HTMLTextAreaElement;
        debounceMs?: number;
        onDebouncedInput: () => void;
    }): void {
        const { postKey, formRoot, contentTextarea, debounceMs = 300, onDebouncedInput } = params;
        if (!contentTextarea) return;

        const scope = getEventScope(formRoot || document, `post-sns-content:${String(postKey)}`);

        scope.once("sns-content-autodetect", () => {
            let timer: ReturnType<typeof setTimeout> | null = null;

            contentTextarea.addEventListener("input", () => {
                const panel = SNSPostListener.findPanelInRoot(formRoot || document, postKey);
                if (panel && panel.classList.contains("is-editing")) {
                    return;
                }

                if (timer) {
                    clearTimeout(timer);
                }

                timer = setTimeout(() => {
                    try {
                        onDebouncedInput();
                    } catch (error) {
                        console.warn("[SNS Post] SNS auto-detect failed", error);
                    }
                }, debounceMs);
            });
        });
    }
}
